import { useCallback, useMemo, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { CheckoutPaymentMethod } from '@tiendanube/common/src/enums';
import { toStatusType } from 'App/rtk';
import { convertStatusTypeToObject } from 'commons/utils/convertStatusTypeToObject';
import { CheckoutContext } from 'domains/Billing/Checkout/CheckoutUI';
import { getConceptsFromCharges, getMainConceptFromCharges } from './utils';
import {
  useGetPayOrderToPayQuery,
  usePostCreatePayOrderMutation,
} from '../CheckoutApi';
import { FEE_CONCEPTS } from '../pages/utils';

function usePayorderToPay(initialFetch = true) {
  const { payOrderId: payOrderIdFromParams } =
    useParams<{ payOrderId: string }>() || '';
  const { payOrderId: payOrderIdFromContext } = useContext(CheckoutContext);
  const payOrderId = payOrderIdFromParams || payOrderIdFromContext;

  const {
    status,
    data: payorderToPay,
    refetch,
  } = useGetPayOrderToPayQuery(payOrderId, {
    skip: !payOrderId || !initialFetch,
  });

  const [createPayorder, { status: payOrderCreationStatus }] =
    usePostCreatePayOrderMutation();

  const conceptsToPay = useMemo(
    () => payorderToPay && getConceptsFromCharges(payorderToPay.charges),
    [payorderToPay],
  );

  const mainConcept =
    payorderToPay && getMainConceptFromCharges(payorderToPay.charges);

  const isForCurrentPlan = useMemo(
    () =>
      payorderToPay?.charges.some(
        ({ status, conceptCode }) =>
          conceptCode === 'plan-cost' && status !== 'CHARGED_BACK',
      ),
    [payorderToPay],
  );

  const totalWithoutFees = useMemo(
    () =>
      payorderToPay?.charges.reduce(
        (acc, { conceptCode, amountValue }) =>
          FEE_CONCEPTS.includes(conceptCode) ? acc : acc + Number(amountValue),
        0,
      ),
    [payorderToPay],
  );

  const subtotalWithoutFees = useMemo(
    () =>
      payorderToPay?.charges.reduce(
        (acc, { conceptCode, amountBaseValue }) =>
          FEE_CONCEPTS.includes(conceptCode) ? acc : acc + amountBaseValue,
        0,
      ),
    [payorderToPay],
  );

  const feesForMethod = useCallback(
    (selectedMethod: CheckoutPaymentMethod) =>
      payorderToPay?.paymentFees.filter(
        ({ method }) => method === selectedMethod,
      ),
    [payorderToPay],
  );

  const totalFeeForMethod = useCallback(
    (selectedMethod: CheckoutPaymentMethod) =>
      feesForMethod(selectedMethod)?.reduce(
        (acc, fee) => acc + Number(fee.amountValue),
        0,
      ),
    [feesForMethod],
  );

  const totalFeeForMethodWithTaxes = useCallback(
    (selectedMethod: CheckoutPaymentMethod) =>
      feesForMethod(selectedMethod)?.reduce(
        (acc, fee) =>
          acc +
          Number(fee.amountValue) +
          fee.taxes.reduce(
            (acc, { amountValue }) => acc + Number(amountValue),
            0,
          ),
        0,
      ),
    [feesForMethod],
  );

  const feePercentageForMethod = useCallback(
    (selectedMethod: CheckoutPaymentMethod) =>
      feesForMethod(selectedMethod)?.[0]?.fee.value ?? 0,
    [feesForMethod],
  );

  const nonFeeCharges = useMemo(
    () =>
      payorderToPay?.charges.filter(
        ({ conceptCode }) => !FEE_CONCEPTS.includes(conceptCode),
      ),
    [payorderToPay],
  );

  const isForPlanOnly = useMemo(
    () =>
      !!conceptsToPay &&
      Array.from(conceptsToPay).every(
        (concept) =>
          concept === 'plan-cost' || concept === 'offline-pos-extra-location',
      ),
    [conceptsToPay],
  );

  return {
    createPayorder,
    payorderToPay,
    status: toStatusType(status),
    refreshPayOrderToPay: refetch,
    payOrderCreationStatus: toStatusType(payOrderCreationStatus),
    ...convertStatusTypeToObject(status),
    payOrderId,
    isForCurrentPlan,
    isForPlanOnly,
    conceptsToPay,
    recurrentPaymentCompatible: payorderToPay?.charges.some(
      ({ concept }) => concept.recurrentPaymentCompatible,
    ),
    mainConcept,
    feesForMethod,
    totalFeeForMethod,
    totalFeeForMethodWithTaxes,
    totalWithoutFees,
    subtotalWithoutFees,
    feePercentageForMethod,
    nonFeeCharges,
  };
}

export default usePayorderToPay;
